/**
 * Type definitions for the unified Reports API (v2).
 */

import type { ApiBackofficeSync } from './backoffice-sync';
import type { RegistryDocumentsScope } from './registry-documents';
import type { ScreeningRating } from './screening-outcome';

/**
 * Backend column name: `report.lifecycle_stage` (Phase 02 rename of
 * the previous `status`). Wire field name remains `status` so the
 * SPA's API contract is unchanged. Identification now runs while a
 * Report sits at `triage` (the legacy `awaiting_subject_identification`
 * lifecycle stage was removed).
 *
 * `pending_activation` sits between triage and `queued`: for ReportConfigs with
 * `requires_client_activation`, a triaged report waits here (client-visible,
 * showing only the subject card) until a client or admin presses Start, which
 * activates it (→ `queued`).
 *
 * `in_qa` sits between the content landing and `completed`: under a config
 * that requires approval, the finished report waits here for the admin
 * approve action. It HAS content — admins render it in full to review it.
 * Clients see the coarse `in_progress` instead.
 *
 * `awaiting_profile_review` parks the report before a subject's social
 * media screening is ordered, until a reviewer confirms or rejects the
 * profiles the system could not vouch for. Unlike the other gates it is
 * acted on by the requesting team as well as admins, so clients receive
 * it as-is rather than collapsed into `in_progress`.
 */
export type ReportLifecycleStage =
  | 'triage'
  | 'pending_activation'
  | 'queued'
  | 'processing'
  | 'awaiting_fulfilment'
  | 'processing_feedback'
  | 'awaiting_file_selection'
  | 'awaiting_profile_review'
  | 'awaiting_match_review'
  | 'in_qa'
  | 'completed'
  | 'failed';

/**
 * Coarse client-facing status — the ONLY five values a non-admin ever
 * receives on the wire (the backend collapses the real stage; see
 * `client_stage_for`). `pending_activation`/`completed` reuse the real
 * stage's value so client-side branches keep matching, and
 * `awaiting_profile_review` passes through because the client acts on it
 * (the profile review gate is open to the requesting team).
 */
export type ClientReportStage =
  | 'submitted'
  | 'pending_activation'
  | 'in_progress'
  | 'awaiting_profile_review'
  | 'completed';

/** What the wire `status` field can carry: the real lifecycle stage for
 * admins, the coarse client stage for everyone else. */
export type ReportStatus = ReportLifecycleStage | ClientReportStage;

export type ReportLevel = 'screening' | 'escalated';

/** Approval requirement mode on a ReportConfig. */
export type ApprovalRequirement = 'disabled' | 'enabled';

/** Derived approval state of a report (from the report_approvals event log). */
export type ApprovalStatus = 'approved' | 'pending' | 'revoked';

/** Action recorded on an approve/revoke event. */
export type ApprovalAction = 'approved' | 'revoked';

export type ContentKind = 'markdown' | 'docx';

/** Cover, header and styling of a report's downloaded PDF, chosen per
 * ReportConfig. Null on the config means the GTI default. */
export type ReportBranding = 'gti' | 'perrett-laver';

/** Pipeline depth on a ReportConfig: `screening_only` (basic-screening
 * tier — identification + deep research skipped, client-supplied data is
 * the identity anchor) vs the `full` pipeline. */
export type PipelineMode = 'full' | 'screening_only';

/** Match review on a ReportConfig: which remediation matches an analyst
 * adjudicates before synthesis. `low_confidence` queues retained matches
 * below the config threshold (plus uncertain non-matches and
 * cross-validation demotions); `all` queues every retained match. */
export type MatchReviewMode = 'off' | 'low_confidence' | 'all';

/** Profile review on a ReportConfig: which social media profiles a
 * reviewer (the requesting team or an admin) confirms before the
 * screening is ordered. `unconfirmed` pauses only when the system could
 * not vouch for a profile; `all` pauses on every profile. */
export type ProfileReviewMode = 'off' | 'unconfirmed' | 'all';

export interface ReportResponse {
  id: string;
  subject_ids: string[];
  status: ReportStatus;
  /** Owning team (string id, same space as ReportConfig.team_id / inbox).
   * `null` for team-less / self-served reports. */
  team_id: string | null;
  report_config_id: string | null;
  config_name: string | null;
  content: string | null;
  content_kind?: ContentKind | null;
  artifacts: Record<string, any> | null;
  images: Record<string, string> | null;
  error: string | null;
  thread_id: string | null;
  run_id: string | null;
  trigger: string | null;
  backoffice_project_id: string | null;
  /** Admin-only: the last push of this report's commercials to Backoffice
   * failed (cleared by the next success). */
  backoffice_sync_error?: string | null;
  backoffice_sync_failed_at?: string | null;
  /** Outcome of the push the edit on this response triggered. */
  backoffice_sync?: ApiBackofficeSync | null;
  report_request_id?: string | null;
  /** Parent RR's client-set reference — what clients identify the report
   * by (codenames are admin-only). Null for self-served reports. */
  client_reference?: string | null;
  price_override: string | null;
  /** Effective commercials (report override ?? parent request ?? config
   * default) — what the client was actually quoted. */
  price?: string | null;
  turnaround_time_days?: number | null;
  /** Due date once screening starts: started_at + effective TAT. */
  due_at?: string | null;
  /** ISO 4217 code from the config (USD | GBP | EUR); USD fallback.
   * Null when pricing is stripped for client analysts. */
  currency: string | null;
  approval_required: ApprovalRequirement;
  approval_status: ApprovalStatus | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  is_docx?: boolean;
  /** Report-level docx fill variables (plan 15 / B8): overrides the config's
   * report_metadata_defaults key-by-key at fill time. Admin-editable via
   * PATCH /reports/{id}/metadata (full replace). */
  report_metadata?: Record<string, unknown> | null;
  /** Distinct jurisdiction names across the report's subjects. */
  jurisdictions?: string[];
  /** Whether Armadillo i-doc is locked on a live partner assignment of the
   * report, and whether the report already has orders - gates the
   * registry-documents card and its new orders. Absent on an API without
   * the gate, which `registryDocumentsShown` and
   * `registryDocumentsOrderable` read as available. */
  registry_documents?: RegistryDocumentsScope;
}

export interface ReportStatusResponse {
  id: string;
  status: ReportStatus;
  progress: Record<string, any> | null;
  content: string | null;
  error: string | null;
}

export interface ReportSubjectSummary {
  id: string;
  name: string;
  type: string;
}

export interface ReportSummary {
  id: string;
  status: ReportStatus;
  configName: string | null;
  configSlug: string | null;
  trigger: string | null;
  subjects: ReportSubjectSummary[];
  approvalRequired: ApprovalRequirement;
  approvalStatus: ApprovalStatus | null;
  /**
   * Composed codename — opaque display string. New entities (where the client
   * is known at mint time) read as `{client_code}-{N}` e.g. `ACME-001-1`; older
   * records use the legacy random `adjective-noun-pepper` form. Both coexist
   * (forward-only change, no backfill). NULL only for legacy rows that pre-date
   * the Phase-03 backfill.
   */
  codename: string | null;
  /** Family key for PATCH /codenames — the root codename (parent enquiry's
   * for RR-linked reports, the report's own for self-served). Never derive
   * it by splitting `codename`. Masked (null) for clients, like codename. */
  codenameFamily: string | null;
  /** Parent RR's client-set reference — what clients identify the row by
   * (codenames are admin-only). Null for self-served reports. Optional
   * only so test fixtures stay lean — the transform always sets it. */
  clientReference?: string | null;
  /** Owning team id — admin list context (null for team-less rows). */
  teamId: string | null;
  /** Who asked for the report (linked request's requester) — null for
   * self-served reports. */
  requesterEmail: string | null;
  /** Effective price (report override ?? request ?? config default). */
  price: string | number | null;
  /** ISO 4217 code from the config (USD | GBP | EUR); USD fallback. */
  currency: string;
  /** Effective TAT (report override ?? request ?? config default). */
  turnaroundTimeDays: number | null;
  /** Due date once screening starts: started_at + effective TAT. */
  dueAt: string | null;
  /** Distinct jurisdiction names across the report's subjects. */
  jurisdictions: string[];
  createdAt: string | null;
  completedAt: string | null;
  backofficeProjectId: string | null;
  /** Screening hit rating roll-up. Clients receive it on completed
   * reports only; null before, and while unrated. Optional only so test
   * fixtures stay lean — the transform always sets it. */
  screeningRating?: ScreeningRating | null;
}

/** A single append-only approval event. */
export interface ReportApproval {
  id: string;
  reportId: string;
  status: ApprovalAction;
  performedByUserId: string;
  notes: string | null;
  createdAt: string | null;
}

/** API response (snake_case). */
export interface ApiReportApproval {
  id: string;
  report_id: string;
  status: ApprovalAction;
  performed_by_user_id: string;
  notes: string | null;
  created_at: string | null;
}

/** API response (snake_case) for the paginated pending approvals dashboard. */
export interface ApiReportSummary {
  id: string;
  status: ReportStatus;
  config_name: string | null;
  config_slug: string | null;
  trigger: string | null;
  subjects: ReportSubjectSummary[];
  approval_required: ApprovalRequirement;
  approval_status: ApprovalStatus | null;
  codename: string | null;
  codename_family?: string | null;
  client_reference?: string | null;
  team_id?: string | null;
  requester_email?: string | null;
  price?: string | number | null;
  currency?: string;
  turnaround_time_days?: number | null;
  due_at?: string | null;
  jurisdictions?: string[];
  created_at: string | null;
  completed_at: string | null;
  backoffice_project_id: string | null;
  screening_rating?: ScreeningRating | null;
}

export function transformToReportApproval(api: ApiReportApproval): ReportApproval {
  return {
    id: api.id,
    reportId: api.report_id,
    status: api.status,
    performedByUserId: api.performed_by_user_id,
    notes: api.notes,
    createdAt: api.created_at,
  };
}

export function transformToReportSummary(api: ApiReportSummary): ReportSummary {
  return {
    id: api.id,
    status: api.status,
    configName: api.config_name,
    configSlug: api.config_slug,
    trigger: api.trigger,
    subjects: api.subjects,
    approvalRequired: api.approval_required,
    approvalStatus: api.approval_status,
    codename: api.codename ?? null,
    codenameFamily: api.codename_family ?? null,
    clientReference: api.client_reference ?? null,
    teamId: api.team_id ?? null,
    requesterEmail: api.requester_email ?? null,
    price: api.price ?? null,
    currency: api.currency ?? 'USD',
    turnaroundTimeDays: api.turnaround_time_days ?? null,
    dueAt: api.due_at ?? null,
    jurisdictions: api.jurisdictions ?? [],
    createdAt: api.created_at,
    completedAt: api.completed_at,
    backofficeProjectId: api.backoffice_project_id ?? null,
    screeningRating: api.screening_rating ?? null,
  };
}

export interface ReportVersionSummary {
  id: string;
  version: number;
  status: ReportStatus;
  level: ReportLevel;
  trigger: string | null;
  created_at: string | null;
  completed_at: string | null;
}

/** Request body for `POST /reports/{id}/escalate`. */
export interface ReportEscalationRequest {
  report_config_id: string;
  reason?: string | null;
}

/** Response from `POST /reports/{id}/upload-docx-version`. */
export interface DocxIngestResponse {
  id: string;
  status: ReportLifecycleStage;
}

/** Response (snake_case) from `POST /reports/{id}/escalate`.
 * ``report`` echoes the newly-created (escalated-to) report so the SPA can
 * navigate straight to it. */
export interface ReportEscalationResponse {
  id: string;
  escalated_from_report_id: string;
  escalated_to_report_id: string | null;
  escalated_to_report_request_id: string | null;
  reason: string | null;
  created_at: string | null;
  report: ReportResponse | null;
}

/** Response (snake_case) from
 * `POST /reports/{id}/subjects/{subjectId}/retry-remediation`.
 * Resumes a stuck/failed per-subject remediation LangGraph run on its
 * existing thread rather than restarting the report. */
export interface RetryRemediationResponse {
  status: 'resumed';
  report_id: string;
  subject_id: string;
  thread_id: string;
  run_id: string;
}

export type BackofficeFileType = 'docx' | 'pdf' | 'csv' | 'xlsx' | 'image' | 'other';

export interface BackofficeFile {
  raw_data_key: string;
  filename: string;
  file_type: BackofficeFileType;
  presigned_url: string;
  scope_component_slug: string | null;
  /** True when this file is anchored as "the report" (what clients see). */
  is_current: boolean;
  /** "submission" = NP-submitted doc (anchorable when docx);
   * "current_report" = the report's own latest content docx, pinned first;
   * "ferretly_report" = Ferretly's PDF screening report — admin-only (it holds
   * the subject's social posts), never shareable or anchorable;
   * "overflow" = the whole of a list a report section had to cap, shown to
   * the client whenever the report is — never toggled or anchorable. */
  kind: 'submission' | 'current_report' | 'ferretly_report' | 'overflow';
  /** Admin-ticked "share with client" — clients see flagged submissions
   * (upon approval) in their own Documents tab. */
  client_visible: boolean;
  /** When the file landed (ISO). Null for the pinned current-report entry. */
  created_at: string | null;
  /** True when the file arrived after the assignment's latest
   * send-back-to-partner, i.e. it is part of a resubmission. */
  is_resubmission: boolean;
}

/** How one attempt at a piece of graph work ended. */
export type RunAttemptOutcome = 'failed' | 'pending' | 'succeeded';

/**
 * One attempt against a (report, subject, graph): the original failure is
 * attempt 0, each retry appends. Admin-only — raw exceptions and node
 * names are operational detail, never client-facing.
 *
 * Mirrors `ReportRunFailureRead` in `vnext/schemas/report_run_failure.py`.
 */
export interface ReportRunFailure {
  id: string;
  subject_id: string | null;
  /** Joined server-side so a multi-subject report can name which subject
   *  died without a second round-trip. */
  subject_name: string | null;
  graph_id: string | null;
  attempt_no: number;
  outcome: RunAttemptOutcome;
  /** LangGraph's own verdict — 'error' | 'timeout' | 'interrupted'. A
   *  timeout is not a crash, and the message alone can't tell you which. */
  run_status: string | null;
  failing_node: string | null;
  error_type: string | null;
  error_message: string | null;
  /** Ids, not a URL: a LangSmith deep link needs the tenant and project,
   *  which the API doesn't hold. A wrong link is worse than a copyable id. */
  run_id: string | null;
  thread_id: string | null;
  triggered_by_user_id: string | null;
  created_at: string | null;
  resolved_at: string | null;
}

/** Result of POST /reports/{id}/retry. */
export interface RetryFailedRunResponse {
  next_action: string | null;
  report_status: ReportStatus;
  retried_subject_id: string | null;
}
